import { css } from "styled-components";

const CONTROL_TRANSITION = "120ms ease";
// 포인터로 직접 조작하는 스냅 후보는 일반 상태 전환보다 짧고 일정하게 따라가야 한다.
const DIRECT_MANIPULATION_TRANSITION = "60ms linear";

export const controlRadius = css`
  border-radius: ${({ theme }) => theme.shape.radius.control};
`;

export const controlTransition = (...properties: string[]) => css`
  transition: ${properties.map((property) => `${property} ${CONTROL_TRANSITION}`).join(",\n")};
`;

export const directManipulationTransition = (...properties: string[]) => css`
  transition: ${properties
    .map((property) => `${property} ${DIRECT_MANIPULATION_TRANSITION}`)
    .join(",\n")};
`;

export const controlFocusRing = (outlineOffset = "2px") => css`
  outline: 2px solid ${({ theme }) => theme.color.border.focus};
  outline-offset: ${outlineOffset};
`;

// 외곽 박스를 만드는 한 줄 inline element의 공용 절대 높이. 같은 key는 LabelChip,
// IconButton, 폼 컨트롤 등 component family와 관계없이 같은 outer height를 뜻한다. 기존
// IconButton/Input의 public size 이름과 geometry를 기준점으로 삼아 silent breaking change를
// 피한다. 각 family는 필요한 subset만 노출한다. height는 theme-invariant라 theme token/CSS
// variable이 아니라 내부 foundation 상수로 둔다(CONTROL_TRANSITION과 같은 판단).
export const inlineElementHeight = {
  xs: "1.5rem",
  sm: "1.75rem",
  md: "2rem",
  lg: "2.5rem",
} as const;

export type InlineElementSize = keyof typeof inlineElementHeight;

// 공용 outer-height 안에 놓이는 단일 행 콘텐츠/값 텍스트의 metric. 높이 key와 같은 절대
// vocabulary를 사용하되, 기존 role 기반 typography 토큰만 조합한다. xs/sm은 dense caption,
// md/lg는 기본 control body 크기이며, 모두 한 줄 control 안에서 수직 여백을 안정적으로
// 유지하도록 tight line-height를 쓴다. weight는 selection/label identity 의미에 따라 component가
// 계속 소유한다. 굵기가 이미 강조를 담당하는 action label과 아이콘·글리프는 아래 role scale이
// 별도 축으로 관리한다.
export const inlineElementTypography = {
  xs: {
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  sm: {
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  md: {
    fontSize: "var(--c6-typography-size-body)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  lg: {
    fontSize: "var(--c6-typography-size-body)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
} as const satisfies Record<
  InlineElementSize,
  { readonly fontSize: string; readonly lineHeight: string }
>;

// 컨트롤 위 action label의 metric. action weight(bold)가 이미 강조를 담당하므로 xs/sm/md는
// caption floor를 지키고, 여백이 충분한 lg에서만 body로 오른다. 콘텐츠/값 텍스트와 역할을
// 분리하면서도 기존 IconButton 배포 계약(12/12/12/14px + 전 tier tight)을 한 곳에 모은다.
export const inlineElementActionTypography = {
  xs: {
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  sm: {
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  md: {
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
  lg: {
    fontSize: "var(--c6-typography-size-body)",
    lineHeight: "var(--c6-typography-line-height-tight)",
  },
} as const satisfies Record<
  InlineElementSize,
  { readonly fontSize: string; readonly lineHeight: string }
>;

// control의 의미를 직접 전달하는 primary glyph. c6 glyph는 1em SVG이므로 소비 component가 이 값을
// font-size(em basis)로 적용한다. 같은 outer-size에서 IconButton과 ContentSwitcher의 primary icon이
// 같은 silhouette을 갖도록 기존 14/14/16/20px 계약을 한 곳에 모은다.
export const inlineElementIconSize = {
  xs: "var(--c6-typography-size-body)",
  sm: "var(--c6-typography-size-body)",
  md: "var(--c6-typography-size-title)",
  lg: "var(--c6-typography-size-head)",
} as const satisfies Record<InlineElementSize, string>;

// field/label 안에서 주 콘텐츠를 보조하는 trailing indicator·decorative glyph. 텍스트와 함께 읽히는
// 역할이라 xs/sm은 caption(12px), md는 body(14px)에 맞춘다. 현재 adopter가 없는 lg 값은 미리
// 예약하지 않는다. glyph artwork 자체의 optical 보정(width/height multiplier)은 component 책임이다.
export const inlineElementIndicatorSize = {
  xs: "var(--c6-typography-size-caption)",
  sm: "var(--c6-typography-size-caption)",
  md: "var(--c6-typography-size-body)",
} as const;

// 텍스트 IconButton 의 공용 좌우 패딩. 높이처럼 theme-invariant 상수. 라벨 크기는 이 스케일이
// 만들지 않는다. 세로는 min-height가 잡고, 좌우 값은 기존 IconButton geometry와 소비처 filter
// bar 밀도를 보존한다. typography 정규화에서도 이 padding 계약은 바꾸지 않는다. 필드형 컨트롤
// (Input/SearchInput/Picker)은 자체 패딩을 유지한다.
export const controlPadX = {
  xs: "0.5rem",
  sm: "0.75rem",
  md: "1rem",
  lg: "1.25rem",
} as const;

// 컨트롤 세로 패딩의 공용 값. 높이는 inlineElementHeightStyles(min-height)가 소유하고 콘텐츠는
// flex 중앙 정렬되므로, "콘텐츠 + 2×padY + 테두리 ≤ inlineElementHeight" 인 한 padY 는 시각적 no-op 인
// slack 이다 — 그런데도 상수인 이유: (1) icon-only IconButton 은 이 값을 가로에도 대칭 적용해
// min 정사각(24/28/32/40)을 만드는 기하 계약이고, (2) 소비처마다 제각각이던 slack 값에 "이 값
// 이하면 안전"의 기준선을 준다. 단일 고정값이 아닌 이유: size 별 글리프(14/14/16/20)와
// min(24/28/32/40)이 달라 정사각 조건이 size 마다 다르다.
// 주의: min-height 계약 밖의 소비처 — 고정 height 셸 안에서 height:100% 콘텐츠가 이 패딩을
// 나눠 먹는 구조(MultiSelectDropdown 커스텀 트리거) — 에서는 padY 인상이 no-op 이 아니므로
// 자체 padY 를 유지한다.
export const controlPadY = {
  xs: "0.1875rem",
  sm: "0.3rem",
  md: "0.375rem",
  lg: "0.5rem",
} as const;

// size별 높이 계약을 컨트롤에 합성한다. min-height라 내용이 넘치면 늘어난다(토큰이 여러 줄로
// wrap되는 TokenSearchInput 등) — 단일 줄이면 이 높이로 고정. box-sizing:border-box라 테두리가
// 높이에 포함된다. 소비처는 세로 패딩을 이 높이 이하로 유지해야 min-height가 실제로 바인딩된다
// (패딩이 크면 단일 줄에서도 넘쳐 계약보다 커진다 — 기존 Input 36px 회귀의 원인).
export const inlineElementHeightStyles = (size: InlineElementSize) => css`
  box-sizing: border-box;
  min-height: ${inlineElementHeight[size]};
`;
